import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  TrendingUp, DollarSign, Package, BarChart3, RefreshCw, ArrowUpRight, ArrowRight, Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Forecast() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("30");

  const fetchForecast = async (period) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/reports/forecast?days=${period}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      setData(await res.json());
    } catch { toast.error("Failed to load forecast"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchForecast(days); }, [days]);

  const fmt = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v || 0);

  return (
    <Layout>
      <div className="space-y-6" data-testid="forecast-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-primary" />
              Sales Forecast
            </h1>
            <p className="text-muted-foreground">AI-powered predictions based on historical sales data</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-40 rounded-sm" data-testid="forecast-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Next 7 Days</SelectItem>
                <SelectItem value="14">Next 14 Days</SelectItem>
                <SelectItem value="30">Next 30 Days</SelectItem>
                <SelectItem value="60">Next 60 Days</SelectItem>
                <SelectItem value="90">Next 90 Days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => fetchForecast(days)} className="rounded-sm" data-testid="refresh-forecast-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary mb-3" />
            <p className="text-muted-foreground">Generating forecast...</p>
          </div>
        ) : data ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="rounded-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-emerald-500/10">
                      <DollarSign className="h-5 w-5 text-emerald-500" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Forecast Revenue</p>
                      <p className="text-xl font-semibold font-data text-emerald-600">{fmt(data.summary.forecast_revenue)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="rounded-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-blue-500/10">
                      <Package className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Forecast Units</p>
                      <p className="text-xl font-semibold font-data">{data.summary.forecast_quantity}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="rounded-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-purple-500/10">
                      <BarChart3 className="h-5 w-5 text-purple-500" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Avg Daily Revenue</p>
                      <p className="text-xl font-semibold font-data">{fmt(data.summary.avg_daily_revenue)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="rounded-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-orange-500/10">
                      <ArrowUpRight className="h-5 w-5 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Historical Revenue</p>
                      <p className="text-xl font-semibold font-data">{fmt(data.summary.total_historical_revenue)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Product Forecasts */}
              <Card className="rounded-sm lg:col-span-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Product Forecast ({days} days)</CardTitle>
                  <CardDescription>Predicted sales by product</CardDescription>
                </CardHeader>
                <CardContent>
                  {data.product_forecasts.length === 0 ? (
                    <div className="text-center py-8">
                      <BarChart3 className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                      <p className="text-muted-foreground">No sales data yet. Start recording sales to see forecasts.</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Product</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead className="text-right font-data">Daily Rate</TableHead>
                          <TableHead className="text-right font-data">Forecast Qty</TableHead>
                          <TableHead className="text-right font-data">Forecast Revenue</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.product_forecasts.map((p, i) => (
                          <TableRow key={i} data-testid={`forecast-row-${i}`}>
                            <TableCell className="font-medium">{p.product_name}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{p.category || "Other"}</Badge>
                            </TableCell>
                            <TableCell className="text-right font-data">{p.daily_rate}/day</TableCell>
                            <TableCell className="text-right font-data font-semibold">{p.forecast_quantity}</TableCell>
                            <TableCell className="text-right font-data font-semibold text-emerald-600">
                              {fmt(p.forecast_revenue)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Category Breakdown */}
              <Card className="rounded-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">By Category</CardTitle>
                  <CardDescription>Historical performance</CardDescription>
                </CardHeader>
                <CardContent>
                  {data.category_breakdown.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">No data</p>
                  ) : (
                    <div className="space-y-3">
                      {data.category_breakdown.map((c, i) => (
                        <div key={i} className="flex items-center justify-between p-3 rounded-md border" data-testid={`category-${i}`}>
                          <div>
                            <p className="font-medium text-sm">{c.category}</p>
                            <p className="text-xs text-muted-foreground">{c.sales_count} sales, {c.quantity} units</p>
                          </div>
                          <p className="font-data font-semibold text-emerald-600">{fmt(c.revenue)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Daily Trend */}
            {data.daily_trend.length > 0 && (
              <Card className="rounded-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Daily Revenue Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end gap-1 h-32">
                    {data.daily_trend.map((d, i) => {
                      const maxRev = Math.max(...data.daily_trend.map(x => x.revenue));
                      const height = maxRev > 0 ? (d.revenue / maxRev) * 100 : 0;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative" data-testid={`trend-bar-${i}`}>
                          <div
                            className="w-full bg-primary/20 hover:bg-primary/40 rounded-t transition-colors min-h-[2px]"
                            style={{ height: `${Math.max(height, 2)}%` }}
                          />
                          <div className="absolute -top-8 bg-popover border rounded px-2 py-1 text-xs font-data opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                            {d.date}: {fmt(d.revenue)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-muted-foreground font-data">
                    <span>{data.daily_trend[0]?.date}</span>
                    <span>{data.daily_trend[data.daily_trend.length - 1]?.date}</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : null}
      </div>
    </Layout>
  );
}
